// MCP Security — Metric card.
//
// Visual family-match with WAF's WAFOverviewMetricCard (see
// src/components/pages/_new/services/web_application/components/
// WAFOverviewMetricCard.js):
//
//   - 1px neutral[200] border, 8px radius, white ground.
//   - Label row: 10.5px uppercase, 0.07em tracking, indigo #6371F7.
//   - Value row: 20..28px tabular-nums, neutral[1000].
//   - Optional delta sub-row and "New" pill retained from the MCP mockup.
//
// The props stay identical to the previous MetricCard so no page call
// site changes shape.

import React from "react";
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import tokens from "./tokens";

const ACCENT = tokens.primaryIndigo; // == colors.primary[300]

/** Label row style — the WAF overview strip's uppercase accent. */
export const metricLabelSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: ACCENT,
  lineHeight: 1.3,
};

export default function MetricCard({
  label,
  value,
  delta,
  deltaFg,
  pills,
  sub,
  tip,
  isNew,
  minHeight = 118,
}) {
  const inner = (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${tokens.neutral[200]}`,
        borderRadius: tokens.cardRadius,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        minHeight,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography sx={{ flex: 1, ...metricLabelSx }}>{label}</Typography>
        {isNew && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              padding: "1px 6px",
              borderRadius: 999,
              background: tokens.primary[100],
              color: tokens.primaryIndigoInk,
              whiteSpace: "nowrap",
            }}
          >
            New
          </span>
        )}
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.75 }}>
        <Typography
          sx={{
            fontSize: 28,
            lineHeight: "34px",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: tokens.neutral[1000],
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </Typography>
        {delta && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "nowrap",
              color: deltaFg || tokens.neutral[700],
            }}
          >
            {delta}
          </Typography>
        )}
      </Stack>
      {pills && pills.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {pills.map((p, i) => (
            <span
              key={i}
              style={{
                fontSize: 11,
                padding: "1px 8px",
                borderRadius: 999,
                background: p.bg || tokens.neutral[150],
                color: p.fg || tokens.neutral[800],
                whiteSpace: "nowrap",
              }}
            >
              {p.t}
            </span>
          ))}
        </Box>
      )}
      {sub && (
        <Typography sx={{ fontSize: 12, lineHeight: "18px", color: tokens.neutral[700] }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
  return tip ? <Tooltip title={tip}>{inner}</Tooltip> : inner;
}
